import type {
  StudioAudience,
  StudioTemplate,
  StudioTemplateListFilter,
  StudioTemplateStatus,
  StudioTemplateVersion,
  StudioTemplateVersionStatus,
  StudioTemplateVisibility,
} from '@/api/adminClient';
import type { StatusTone } from '@/lib/runTelemetry';

export const TEMPLATE_STATUS_META: Record<StudioTemplateStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  in_review: { label: 'In review', tone: 'active' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Changes requested', tone: 'warning' },
  published: { label: 'Live', tone: 'success' },
  retired: { label: 'Retired', tone: 'neutral' },
};

export const VERSION_STATUS_META: Record<StudioTemplateVersionStatus, { label: string; tone: StatusTone }> = {
  pending_review: { label: 'Waiting for review', tone: 'active' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Changes requested', tone: 'warning' },
  published: { label: 'Live', tone: 'success' },
  superseded: { label: 'Replaced', tone: 'neutral' },
};

export const VISIBILITY_OPTIONS: Array<{ id: StudioTemplateVisibility; label: string; hint: string }> = [
  { id: 'all_orgs', label: 'Every organization', hint: 'Every Studio customer sees it in Templates.' },
  { id: 'org_list', label: 'Chosen organizations', hint: 'Only the organizations listed below see it.' },
  { id: 'internal', label: 'RapidSOS only', hint: 'Only the publishing organization sees it. Good for a trial run.' },
];

export const AUDIENCE_OPTIONS: Array<{ id: StudioAudience; label: string; hint: string }> = [
  { id: 'everyone', label: 'Everyone', hint: 'Anyone the visibility above lets in.' },
  { id: 'internal', label: 'Internal', hint: 'Only people with Internal Access see it, and it can be used only in RapidSOS organizations.' },
];

export const TEMPLATE_PAGE_SIZE = 25;

export const TEMPLATE_FILTERS: Array<{ id: StudioTemplateListFilter; label: string }> = [
  { id: 'review', label: 'Needs review' },
  { id: 'live', label: 'Live' },
  { id: 'draft', label: 'Not live yet' },
  { id: 'retired', label: 'Retired' },
  { id: 'all', label: 'All' },
];

export const TEMPLATE_EMPTY_LABELS: Record<StudioTemplateListFilter, string> = {
  review: 'Nothing is waiting for review',
  live: 'No templates are live yet',
  draft: 'Every template is live or retired',
  retired: 'No templates have been retired',
  all: 'No templates yet',
};

/** The list's place in the address: `q`, `status` (absent until chosen) and `page` from 1. */
export type TemplateListState = { q: string; status?: StudioTemplateListFilter; page: number };

export function templateListStateFrom(params: URLSearchParams): TemplateListState {
  const status = params.get('status');
  const page = Number(params.get('page'));
  return {
    q: params.get('q')?.trim() ?? '',
    status: TEMPLATE_FILTERS.find((option) => option.id === status)?.id,
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

export function templateListParams(state: TemplateListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.q) params.set('q', state.q);
  if (state.status) params.set('status', state.status);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}

/** The version a curator acts on next: one waiting for review, else one approved but not yet live. */
export function actionableVersion(versions: StudioTemplateVersion[]): StudioTemplateVersion | undefined {
  return versions.find((version) => version.status === 'pending_review') ?? versions.find((version) => version.status === 'approved');
}

/** Why a version can't be published yet, or undefined when it can. */
export function publishBlocker(template: StudioTemplate, version: StudioTemplateVersion): string | undefined {
  if (version.status !== 'approved') return 'Approve this version first.';
  if (!version.storage) return 'This version was not copied into template storage.';
  if (!template.summary.trim()) return 'Add a short summary to the listing first.';
  return undefined;
}

/** Comma- or newline-separated org ids, deduplicated. */
export function parseOrgIds(value: string): string[] {
  return [...new Set(value.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean))];
}

export function parseTags(value: string): string[] {
  return [...new Set(value.split(',').map((tag) => tag.trim()).filter(Boolean))].slice(0, 10);
}

/** "Uses Google Maps, SMS · Has a backend" — what a customer will see before they use it. */
export function needsSummary(needs: StudioTemplateVersion['needs']): string {
  const parts: string[] = [];
  if (needs.integrations.length) parts.push(`Uses ${needs.integrations.map((need) => need.integrationId).join(', ')}`);
  if (needs.hasBackend) parts.push('Has a backend');
  return parts.join(' · ') || 'No integrations';
}
